'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';

const KPITrendChart = dynamic(() => import('./KPITrendChart'), { ssr: false });
const ForecastChart = dynamic(() => import('./ForecastChart'), { ssr: false });
const CorrelationHeatmap = dynamic(() => import('./CorrelationHeatmap'), { ssr: false });

interface Anomaly {
  id: string;
  metric: string;
  bvo: string;
  deviation: string;
  severity: 'high' | 'medium' | 'low';
  detected: string;
  value: string;
  expected: string;
}

const anomalies: Anomaly[] = [
  { id: 'anom-001', metric: 'Cycle Time P95', bvo: 'Customer Order', deviation: '+42%', severity: 'high', detected: '2h ago', value: '8.1d', expected: '5.7d' },
  { id: 'anom-002', metric: 'Manual Override Rate', bvo: 'Supplier Invoice', deviation: '+28%', severity: 'high', detected: '4h ago', value: '14.4%', expected: '11.2%' },
  { id: 'anom-003', metric: 'Cash Application Match Rate', bvo: 'Cash Application', deviation: '-11%', severity: 'medium', detected: '6h ago', value: '74.3%', expected: '83.6%' },
  { id: 'anom-004', metric: 'Invoice Volume Spike', bvo: 'Supplier Invoice', deviation: '+34%', severity: 'medium', detected: '1d ago', value: '1,205/mo', expected: '900/mo' },
  { id: 'anom-005', metric: 'DPO Variance', bvo: 'Working Capital', deviation: '+18%', severity: 'low', detected: '2d ago', value: '38.4d', expected: '32.5d' },
];

const statSummary = [
  { id: 'stat-r2', label: 'R² (Cycle Time Model)', value: '0.84', subtext: 'Strong fit', positive: true },
  { id: 'stat-pval', label: 'P-Value (Conformance)', value: '0.003', subtext: 'Statistically significant', positive: true },
  { id: 'stat-acc', label: 'Forecast Accuracy (MAPE)', value: '8.2%', subtext: 'Last 30 days', positive: true },
  { id: 'stat-anom', label: 'Anomalies Detected', value: String(anomalies.length), subtext: '2 high severity', positive: false },
];

const severityConfig = {
  high: { color: 'text-negative', bg: 'bg-negative/10 border-negative/20', icon: 'ExclamationCircleIcon' },
  medium: { color: 'text-warning', bg: 'bg-warning/10 border-warning/20', icon: 'ExclamationTriangleIcon' },
  low: { color: 'text-primary', bg: 'bg-primary/10 border-primary/20', icon: 'InformationCircleIcon' },
};

const dimensions = ['All BVOs', 'Financial Close', 'Cash Application', 'Customer Order', 'Supplier Invoice', 'Working Capital'];
const metrics = ['Cycle Time', 'Conformance Rate', 'Throughput', 'Value at Risk', 'Health Score'];

export default function AnalyticsCenterContent() {
  const [dimension, setDimension] = useState('All BVOs');
  const [metric, setMetric] = useState('Cycle Time');
  const [dateRange, setDateRange] = useState('90d');

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-card rounded-xl card-glow px-5 py-3">
        <Icon name="FunnelIcon" size={14} className="text-muted-foreground shrink-0" />
        <select
          value={dimension}
          onChange={(e) => setDimension(e.target.value)}
          className="bg-input border border-border rounded-lg px-3 py-1.5 text-sm text-foreground outline-none cursor-pointer hover:border-primary/40 transition-colors"
        >
          {dimensions.map((d) => (
            <option key={`dim-${d}`} value={d}>{d}</option>
          ))}
        </select>

        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          className="bg-input border border-border rounded-lg px-3 py-1.5 text-sm text-foreground outline-none cursor-pointer hover:border-primary/40 transition-colors"
        >
          {metrics.map((m) => (
            <option key={`metric-${m}`} value={m}>{m}</option>
          ))}
        </select>

        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {['30d', '90d', '6mo', '1yr'].map((range) => (
            <button
              key={`range-${range}`}
              onClick={() => setDateRange(range)}
              className={`px-3 py-1 rounded text-xs font-medium transition-all duration-150 ${
                dateRange === range ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {range}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted px-3 py-1.5 rounded-lg border border-border transition-colors">
            <Icon name="ArrowDownTrayIcon" size={12} />
            Export Report
          </button>
        </div>
      </div>

      {/* Stat Summary Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {statSummary.map((stat) => (
          <div key={stat.id} className={`bg-card rounded-xl p-4 ${stat.positive ? 'card-glow' : 'card-glow-warning'}`}>
            <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
            <p className={`text-2xl font-bold font-tabular ${stat.positive ? 'text-foreground' : 'text-warning'}`}>{stat.value}</p>
            <p className={`text-xs mt-1 ${stat.positive ? 'text-positive' : 'text-warning'}`}>{stat.subtext}</p>
          </div>
        ))}
      </div>

      {/* Primary KPI Trend Chart */}
      <KPITrendChart metric={metric} dimension={dimension} />

      {/* Bottom Row: Correlation + Forecast + Anomalies */}
      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
        <div className="xl:col-span-1">
          <CorrelationHeatmap />
        </div>
        <div className="xl:col-span-1">
          <ForecastChart />
        </div>
        <div className="xl:col-span-1">
          {/* Anomaly Feed */}
          <div className="bg-card rounded-xl card-glow overflow-hidden h-full flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">Anomaly Detection</h3>
                <span className="bg-negative/15 text-negative text-2xs font-semibold px-2 py-0.5 rounded-full">{anomalies.length}</span>
              </div>
              <span className="text-2xs text-muted-foreground">Last 7 days</span>
            </div>
            <div className="flex-1 divide-y divide-border overflow-y-auto">
              {anomalies.map((anom) => {
                const cfg = severityConfig[anom.severity];
                return (
                  <div key={anom.id} className="px-5 py-3 hover:bg-muted/20 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-lg border shrink-0 mt-0.5 ${cfg.bg}`}>
                        <Icon name={cfg.icon as Parameters<typeof Icon>[0]['name']} size={12} className={cfg.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-xs font-medium text-foreground truncate">{anom.metric}</p>
                          <span className={`text-2xs font-semibold ${cfg.color}`}>{anom.deviation}</span>
                        </div>
                        <p className="text-2xs text-primary">{anom.bvo}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-2xs text-muted-foreground">Actual: <strong className="text-foreground font-tabular">{anom.value}</strong></span>
                          <span className="text-2xs text-muted-foreground">Expected: <strong className="text-muted-foreground font-tabular">{anom.expected}</strong></span>
                        </div>
                        <p className="text-2xs text-muted-foreground mt-0.5">{anom.detected}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}